import { useMemo } from 'react';
import { Avatar, Box, Chip, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  type UseTeamMemberEntitiesResult,
  useTeamMemberEntities,
} from '../shared/useTeamMemberEntities';
import { countryCodeToFlag } from '../../utils/countryFlag';
import { isMemberTitle, MEMBER_TITLE_LABELS, MemberTitle } from '../../utils/memberTitles';

export const MEMBER_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.annotations',
  'spec.profile',
];

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
  },
  divider: { margin: 0 },
  meta: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
}));

interface ResolvedMember {
  ref: string;
  name: string;
  picture?: string;
  role?: string;
  office?: string;
  country?: string;
  title?: MemberTitle;
}

/** Compose "Role · Office" for the meta line; gracefully omits missing parts. */
function metaLine(role?: string, office?: string): string {
  return [role, office].filter(Boolean).join(' · ');
}

/** Convert a Backstage entity ref to its catalog URL, honouring custom namespace/kind. */
function entityRefToCatalogPath(ref: string): string {
  const { kind, namespace, name } = parseEntityRef(ref, {
    defaultKind: 'user',
    defaultNamespace: 'default',
  });
  return `/catalog/${namespace.toLowerCase()}/${kind.toLowerCase()}/${name}`;
}

export interface EntityTeamMembersCardProps {
  /** Pre-fetched member data to reuse instead of issuing a second
   * `getEntitiesByRefs` request — passed by `EntityTeamRosterSection`, which
   * fetches once and shares the result with `EntityTeamRosterHeader`. When
   * omitted (e.g. the card is rendered standalone), the card fetches for
   * itself as before. */
  members?: UseTeamMemberEntitiesResult;
}

/** Members list enriched with role + office from PAT-422 annotations. */
export function EntityTeamMembersCard({ members: injected }: EntityTeamMembersCardProps = {}) {
  const classes = useStyles();
  const { entity } = useEntity();
  const ownFetch = useTeamMemberEntities(MEMBER_FIELDS, { enabled: !injected });
  const { items, loading, error } = injected ?? ownFetch;

  const members: ResolvedMember[] = useMemo(() => {
    const memberTitles =
      (entity.spec as { memberTitles?: Record<string, MemberTitle> } | undefined)
        ?.memberTitles ?? {};
    const rows = items.map(u => {
      const ref = stringifyEntityRef({
        kind: 'User',
        namespace: u.metadata.namespace ?? 'default',
        name: u.metadata.name,
      });
      return {
        ref,
        name: u.spec?.profile?.displayName ?? u.metadata.name,
        picture: u.spec?.profile?.picture,
        role: u.metadata.annotations?.['user/role'],
        office: u.metadata.annotations?.['user/office'],
        country: u.metadata.annotations?.['user/country'],
        title: memberTitles[ref],
      };
    });
    rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  }, [items, entity]);

  let body: JSX.Element;
  if (loading) {
    body = <Progress />;
  } else if (error) {
    body = <ResponseErrorPanel error={error} />;
  } else if (members.length === 0) {
    return null;
  } else {
    body = (
      <>
        {members.map((m, idx) => {
          const meta = metaLine(m.role, m.office);
          const flag = m.country ? countryCodeToFlag(m.country) : '';
          const titleLabel = isMemberTitle(m.title) ? MEMBER_TITLE_LABELS[m.title] : undefined;
          return (
            <Box key={m.ref}>
              {idx > 0 && <Divider className={classes.divider} />}
              <Box className={classes.row}>
                <Avatar src={m.picture} style={{ width: 36, height: 36 }}>
                  {m.name.charAt(0)}
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <Link to={entityRefToCatalogPath(m.ref)}>{m.name}</Link>
                    {flag && (
                      <span role="img" aria-label={`${m.country!.trim().toUpperCase()} flag`}>
                        {flag}
                      </span>
                    )}
                    {titleLabel && (
                      <Chip
                        label={titleLabel}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    )}
                  </Box>
                  {meta && (
                    <Typography className={classes.meta} component="div">
                      {meta}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </>
    );
  }

  // Hide the count during loading and error states so we don't flash
  // "Members (0)" before the real value resolves.
  const title =
    loading || error ? 'Members' : `Members (${members.length})`;

  return (
    <InfoCard title={title} variant="gridItem">
      {body}
    </InfoCard>
  );
}
